<template>
    <div class="flex flex-col gap-y-4">
        <div class="text-2xl font-medium text-gray-900">
            {{ title }}
        </div>

        <gift-card-payment-form
            v-if="giftCardPaymentSourceId !== null"
            :checkout-total="checkoutTotal"
            :payment-source-id="giftCardPaymentSourceId"
        />

        <div
            v-if="showCreditCardInput"
            class="max-w-[650px]"
        >
            <div
                v-if="paymentInformationOptions.length > 1"
                class="flex flex-col flex-wrap gap-1"
            >
                <div
                    v-for="option in paymentInformationOptions"
                    :key="option.key"
                    class="flex items-center"
                >
                    <radio-button
                        v-model="paymentInformationChoice"
                        :value="option.key"
                        :input-id="option.key"
                        name="payment-information"
                    />
                    <label
                        :for="option.key"
                        class="ml-2"
                    >
                        {{ option.text }}
                    </label>
                </div>
            </div>

            <div
                v-else
                class="text-sm font-normal"
            >
                Enter payment details to use for your purchase today &mdash;
                {{ creditCardBalanceToUse.formatCurrency() }}
            </div>

            <template v-if="isStripe">
                <stripe-form
                    v-if="
                        payment.creditCard === null ||
                        payment.creditCard.token === null
                    "
                    ref="stripeFormRef"
                    :payment-source-id="paymentSourceCreditCard.id"
                    :amount="stripeCreditCardAmount"
                    @token="handleToken"
                />

                <prime-message
                    v-else
                    severity="success"
                    :closable="false"
                >
                    Payment Method Added
                </prime-message>
            </template>

            <template v-else>
                <hosted-payment-form
                    v-if="paymentInformationChoice !== 'use-existing'"
                    :payment-source="paymentSourceCreditCard.data"
                    @token="handleToken"
                />

                <div class="text-red-600 ml-3 mt-6">
                    {{ errorPayment || '&nbsp;' }}
                </div>
            </template>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { array, boolean, object, string } from 'yup'
import { Decimal } from '~/composables/support/decimal'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'

export const PaymentInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment PaymentInformationFacilityFragment on Facility {
        ...StoredPaymentInformationFacilityFragment
        cpxPaymentSources {
            id
            data {
                __typename
                ... on PaymentSourceCreditCard {
                    ...HostedPaymentFormPaymentSourceFragment
                }
            }
        }
    }
`)

export const PaymentInformationSchema = object({
    // Put the total in the form so we can use it in validation
    total: string().required(),
    useStoredPaymentMethod: boolean(),
    creditCard: object()
        .shape({
            paymentSourceId: string().required(),
            amount: string().required(),
            token: string().required()
        })
        .nullable()
        .defined(),
    giftCardInputValue: string().max(
        0,
        'Please click apply or remove the entered code'
    ),
    giftCards: array()
        .required()
        .of(
            object().shape({
                currentBalance: string().required(),
                paymentSourceId: string().required(),
                amount: string().required(),
                token: string().required()
            })
        )
}).test(
    'Payment Information',
    'Please enter your credit card details above.',
    value => {
        // Use stored payment method so no need for credit card
        if (value.useStoredPaymentMethod) {
            return true
        }

        const giftCardsNotSufficient =
            // No cards
            value.giftCards === undefined ||
            // Does not cover toal
            value.giftCards
                .reduce(
                    (prev, curr) => prev.minus(new Decimal(curr.amount)),
                    new Decimal(value.total)
                )
                .isNotZero()

        const noCreditCard =
            value.creditCard === null || value.creditCard.token === undefined

        // If gift cards are not sufficient, check for no card
        if (giftCardsNotSufficient && noCreditCard) {
            return false
        }

        return true
    }
)

const paymentInformationSchema = toTypedSchema(PaymentInformationSchema)
type PaymentInformationSchemaType =
    typeof paymentInformationSchema extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never

export type GiftCardPaymentInfo = PaymentInformationSchemaType['giftCards'][0]

export function PaymentInformationInitialValues(total: string) {
    return {
        total,
        creditCard: null,
        giftCardInputValue: '',
        giftCards: []
    }
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import HostedPaymentForm from '~/components/Payments/HostedPaymentForm.vue'
import { useField } from 'vee-validate'
import GiftCardPaymentForm from '~/components/Payments/GiftCardPaymentForm.vue'
import StripeForm from '~/components/Payments/StripeForm.vue'
import PrimeMessage from 'primevue/message'
import RadioButton from 'primevue/radiobutton'

const props = defineProps<{
    facility: FragmentType<typeof PaymentInformationFacilityFragment>
    checkoutTotal: Decimal
    title: string
    allowUseExisting: boolean
}>()

const facilityData = computed(() =>
    getFragmentData(PaymentInformationFacilityFragment, props.facility)
)

const stripeFormRef = ref<InstanceType<typeof StripeForm>>()
async function submitStripeForm() {
    if (stripeFormRef.value !== undefined && stripeFormRef.value !== null) {
        await stripeFormRef.value.submitStripeForm()
    }
}

defineExpose({
    submitStripeForm
})

const paymentSourceCreditCard = computed(() => {
    const paymentSourceCreditCard = facilityData.value.cpxPaymentSources.find(
        source =>
            source.data?.__typename === 'PaymentSourceCreditCard' ||
            source.data?.__typename === 'PaymentSourceCreditCardStripe'
    )

    if (paymentSourceCreditCard === undefined) {
        return null
    }

    return paymentSourceCreditCard
})

const isStripe = computed(() => {
    return (
        paymentSourceCreditCard.value?.data?.__typename ===
        'PaymentSourceCreditCardStripe'
    )
})

const giftCardPaymentSourceId = computed(() => {
    const paymentSourceGiftCard = facilityData.value.cpxPaymentSources.find(
        source => source.data?.__typename === 'PaymentSourceGiftCard'
    )

    if (paymentSourceGiftCard === undefined) {
        return null
    }

    return paymentSourceGiftCard.id
})

const { value: payment, errorMessage: errorPayment } =
    useField<PaymentInformationSchemaType>('payment', '', {
        validateOnValueUpdate: false
    })

const giftCardPaymentTotal = computed(() => {
    return payment.value.giftCards.reduce(
        (accum, giftCard) => accum.plus(new Decimal(giftCard.amount)),
        Decimal.zero()
    )
})

const creditCardBalanceToUse = computed(() => {
    return props.checkoutTotal.minus(giftCardPaymentTotal.value)
})

const stripeCreditCardAmount = computed(() =>
    creditCardBalanceToUse.value.toFixed(2)
)

const showCreditCardInput = computed(() =>
    creditCardBalanceToUse.value.isPositiveAndNotZero()
)

const paymentInformationOptions = computed(() => {
    if (
        paymentSourceCreditCard.value !== null &&
        paymentSourceCreditCard.value.data?.__typename ===
            'PaymentSourceCreditCardStripe'
    ) {
        return [
            {
                key: 'new-credit-card',
                text: 'Enter the credit card to be used for your purchase today'
            }
        ]
    }

    if (!props.allowUseExisting) {
        return [
            {
                key: 'new-credit-card',
                text: 'Enter the credit card to be used for your purchase today'
            }
        ]
    }

    return [
        {
            key: 'use-existing',
            text:
                'Use payment method selected above for ' +
                creditCardBalanceToUse.value.formatCurrency()
        },
        {
            key: 'new-credit-card',
            text: 'Enter new payment method'
        }
    ]
})

if (
    paymentInformationOptions.value.some(value => value.key === 'use-existing')
) {
    payment.value.useStoredPaymentMethod = true
}

const paymentInformationChoice = computed({
    get() {
        return payment.value.useStoredPaymentMethod
            ? 'use-existing'
            : 'new-credit-card'
    },
    set(newValue) {
        payment.value = {
            ...payment.value,
            useStoredPaymentMethod: newValue === 'use-existing'
        }
    }
})

// If we only have one option, turn off the use stored payment method option
watch(paymentInformationOptions, newValue => {
    if (newValue.length < 2) {
        payment.value.useStoredPaymentMethod = false
    }
})

// Update our form data when the checkout total changes
watch(
    () => props.checkoutTotal,
    newTotal => {
        // Our new total as a string
        payment.value = {
            ...payment.value,
            total: newTotal.toFixed(2)
        }
    }
)

watch(creditCardBalanceToUse, newValue => {
    // If we had an existing credit card payment
    if (payment.value.creditCard !== null) {
        // Did the total amount change
        const totalChanged = new Decimal(
            payment.value.creditCard.amount
        ).isNotEqualTo(newValue)

        if (totalChanged) {
            // Update credit card total
            payment.value = {
                ...payment.value,
                creditCard: {
                    ...payment.value.creditCard,
                    amount: newValue.toFixed(2)
                }
            }

            if (isStripe.value) {
                // If stripe, we need to make a new payment intent, so refresh the form
                if (
                    stripeFormRef.value !== undefined &&
                    stripeFormRef.value !== null
                ) {
                    stripeFormRef.value.refreshStripeForm()
                }
                // We've already captured the payment intent so now we need to reset
                else {
                    payment.value = {
                        ...payment.value,
                        creditCard: null
                    }
                }
            }
        }
    }
    // No existing payment, wipe the form to get a new payment intent
    else {
        if (isStripe.value) {
            // If stripe, we need to make a new payment intent, so refresh the form
            if (
                stripeFormRef.value !== undefined &&
                stripeFormRef.value !== null
            ) {
                stripeFormRef.value.refreshStripeForm()
            }
        }
    }
})

function handleToken(token: string | undefined): void {
    if (paymentSourceCreditCard.value === null) {
        throw new Error('Null Stored Payment Source')
    }

    if (token === undefined) {
        payment.value = {
            ...payment.value,
            creditCard: null
        }
    } else {
        payment.value = {
            ...payment.value,
            creditCard: {
                paymentSourceId: paymentSourceCreditCard.value.id,
                amount: creditCardBalanceToUse.value.toFixed(2),
                token
            }
        }
    }
}
</script>
